import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';

import AddressFormComponent from './address-form-component';

const { UI, Order, Customer, MapsService } = Mobi2Go;

class AddressForm extends React.Component {
    static propTypes = {
        closeModal: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired,
    };

    constructor() {
        super();

        this.state = {
            address: null,
            search_query: '',
            service_prediction_list: [],
            saved_address_prediction_list: [],
            force_hide_predictions: false,
            highlighted_prediction_item: null,
            focused_field: null,
            last_focused_field: null,
            is_mouseover_predictions: false,
            emphasis_address_unavailable_message: false,
            is_address_available: false,
            is_address_availability_loading: false,
            wait_time: null,
        };

        Customer.bind('updateAddresses', this.onCustomerAddressesUpdate);
        Order.bind({
            updateCustomer: this.refreshAddress,
            updateAddress: this.refreshAddress,
            updateLocation: this.refreshAddress,
        });
    }

    UNSAFE_componentWillMount() {
        this.refreshAddress();
    }

    componentWillUnmount() {
        Customer.unbind('updateAddresses', this.onCustomerAddressesUpdate);
        Order.unbind({
            updateCustomer: this.refreshAddress,
            updateAddress: this.refreshAddress,
            updateLocation: this.refreshAddress,
        });
    }

    onCustomerAddressesUpdate = () => {
        const { address } = this.state;
        if (address && address.id) {
            // Check that the address still exists on the customer
            try {
                Customer.getAddressById(address.id);
            } catch (e) {
                this.setState(
                    {
                        address: null,
                        search_query: '',
                        wait_time: null,
                    },
                    this.fetchSavedAddressPredictions
                );
            }
        }
        this.fetchSavedAddressPredictions();
    };

    onInputChange = (field, event) => {
        const { value } = event.target;
        const { address } = this.state;
        const new_state = {};

        if (field === 'search_query') {
            new_state.search_query = value;
        } else {
            new_state.address = address.update({ [field]: value });
        }

        this.setState(
            {
                force_hide_predictions: false,
                ...new_state,
            },
            this.fetchPredictions
        );
    };

    onInputFocus = (field, event) => {
        const { target } = event;
        const { focused_field } = this.state;

        this.setState(
            {
                last_focused_field: focused_field,
                focused_field: field,
                force_hide_predictions: false,
                is_mouseover_predictions: false,
            },
            () => {
                if (field === 'search_query') {
                    target.select();
                }
            }
        );
    };

    onInputBlur = field => {
        const { focused_field } = this.state;
        if (focused_field === field) {
            this.setState({
                last_focused_field: focused_field,
                focused_field: null,
            });
        }
    };

    onInputKeyDown = (field, event) => {
        const { key } = event;
        const { highlighted_prediction_item } = this.state;
        const next = key === 'ArrowDown';
        const prev = key === 'ArrowUp';
        const enter = key === 'Enter';

        if (!this.arePredictionsVisible()) {
            return;
        }

        if (enter) {
            this.onPredictionSelect(highlighted_prediction_item);
        }
        if (next || prev) {
            this.movePredictionCursor(next);
        }
    };

    onSaveClick = () => {
        const { address, is_address_available } = this.state;
        const { t, closeModal } = this.props;
        if (!is_address_available) {
            this.emphasisAddressUnavailableMessage();
            return;
        }

        try {
            address.validate();
        } catch (error) {
            UI.centeredAlert({
                title: t('invalid_address'),
                body: error.message,
            });
            return;
        }

        address.save();
        Order.setAddress(address);

        closeModal();
    };

    setAsyncState = state =>
        new Promise(resolve => this.setState(state, resolve));

    setAddress = async (
        new_address,
        search_query,
        reset_service_prediction
    ) => {
        const {
            address,
            service_prediction_list,
            is_address_available,
        } = this.state;
        const was_address_available = address && is_address_available;

        await this.setAsyncState({
            address: new_address,
            is_address_available: false,
            is_address_availability_loading: true,
            force_hide_predictions: true,
            search_query,
            service_prediction_list: reset_service_prediction
                ? []
                : service_prediction_list,
            wait_time: null,
        });

        const estimate = await this.getEstimateForAddress(new_address);

        await this.setAsyncState({
            is_address_availability_loading: false,
            is_address_available: !!estimate,
            wait_time: estimate ? estimate.wait_time : null,
        });

        this.fetchSavedAddressPredictions();
        if (!was_address_available && !is_address_available) {
            setTimeout(this.emphasisAddressUnavailableMessage, 250);
        }
    };

    onPredictionSelect = prediction => {
        if (prediction.is_saved_address) {
            this.setAddress(
                prediction.address,
                prediction.address.toString(),
                true
            );
        } else {
            MapsService.getPlaceDetails(prediction, async details => {
                const address = Order.getCustomer().createAddress();
                address.setFromGmapsPlaceDetail(
                    details,
                    prediction.description
                );

                this.setAddress(address, details.formatted_address, false);

                Order.log('Delivery map polygon address query', {
                    address: address.get()
                });
            });
        }
    };

    onPredictionHighlight = prediction => {
        this.setState({ highlighted_prediction_item: prediction });
    };

    getPredictions() {
        const {
            service_prediction_list,
            saved_address_prediction_list,
        } = this.state;

        return [
            ...service_prediction_list,
            ...saved_address_prediction_list,
        ].sort((a, b) => {
            // Sort so that saved addresses that match the search query are
            // displayed at the top of the list.
            if (a.is_saved_address && a.matched_substrings.length > 0) {
                return -1;
            }
            if (b.is_saved_address && b.matched_substrings.length > 0) {
                return 1;
            }

            return 0;
        });
    }

    onSavedAddressDelete = address => {
        Customer.removeAddressById(address.id);
    };

    getSearchQuery() {
        const {
            search_query,
            focused_field,
            last_focused_field,
            is_mouseover_predictions,
        } = this.state;
        if (
            focused_field === 'search_query' ||
            (last_focused_field === 'search_query' && is_mouseover_predictions)
        ) {
            return search_query;
        }
        return null;
    }

    // eslint-disable-next-line class-methods-use-this
    getEstimateForAddress = async address => {
        const location = Order.getLocation();
        const estimate = await location.getDoorDashDriveEstimate(address);
        return estimate;
    };

    highlightFirstPrediction = () => {
        const predictions = this.getPredictions();

        const highlighted_prediction_item = predictions.length
            ? predictions[0]
            : null;

        this.setState({ highlighted_prediction_item });
    };

    refreshAddress = () => {
        const address = Order.getAddress();

        /**
         * The order location is automatically changed when the address typed in
         * matches another locations delivery address, so instead of clearing the
         * address search, we should check if the address now fits in the new location's
         * delivery area, and if it does, set it on the order. Otherwise clear it as
         * the previous functionality did.
         */
        if (
            !address &&
            this.state.address &&
            Order.getLocation().isAddressAvailable(this.state.address)
        ) {
            Order.setAddress(this.state.address);

            this.setState({
                emphasis_address_unavailable_message: false,
            });
        } else if (!address) {
            this.setState(
                {
                    address,
                    search_query: address ? address.toString() : '',
                    is_address_available: !!address,
                    is_address_availability_loading: false,
                    wait_time: Order.getWaitTime(),
                },
                this.fetchPredictions
            );
        }
    };

    emphasisAddressUnavailableMessage = () => {
        this.setState({
            emphasis_address_unavailable_message: true,
        });

        clearTimeout(this.emphasisAddressUnavailableMessage.animation_timeout);
        this.emphasisAddressUnavailableMessage.animation_timeout = setTimeout(
            () =>
                this.setState({
                    emphasis_address_unavailable_message: false,
                }),
            330
        );
    };

    fetchPredictions = () => {
        this.fetchServicePredictions();
        this.fetchSavedAddressPredictions();
    };

    fetchServicePredictions() {
        const location = Order.getLocation();

        if (!location) {
            return;
        }

        const maps_service = location.getMapsService();
        const search_query = this.getSearchQuery();
        maps_service.getPlacePredictions(
            search_query,
            service_prediction_list => {
                if (this.getSearchQuery() === search_query) {
                    this.setState(
                        {
                            service_prediction_list:
                                service_prediction_list || [],
                        },
                        this.highlightFirstPrediction
                    );
                }
            }
        );
    }

    fetchSavedAddressPredictions() {
        const customer = Order.getCustomer();
        const addresses = customer ? customer.getAddresses() : [];
        const { address: current_address, search_query } = this.state;
        // Remove currently inview address or addresses without coordinates
        const available_addresses = addresses.filter(
            address =>
                address.latitude &&
                address.longitude &&
                address.isSaved() &&
                (!current_address || current_address.id !== address.id) &&
                (address.exclusive_order_id === null ||
                    address.exclusive_order_id === Order.getId())
        );

        const saved_address_prediction_list = available_addresses.map(
            address => {
                const index = address
                    .toString()
                    .toLowerCase()
                    .indexOf(search_query.toLowerCase());
                const matched_substrings = [];
                if (index !== -1) {
                    matched_substrings.push({
                        offset: index,
                        length: search_query.length,
                    });
                }

                return {
                    is_saved_address: true,
                    place_id: address.id,
                    description: address.toString(),
                    matched_substrings,
                    address,
                };
            }
        );

        this.setState(
            { saved_address_prediction_list },
            this.highlightFirstPrediction
        );
    }

    movePredictionCursor(next = true) {
        const predictions = this.getPredictions();
        let { highlighted_prediction_item } = this.state;

        if (highlighted_prediction_item === null && predictions.length) {
            [highlighted_prediction_item] = predictions;
        }

        if (!highlighted_prediction_item) {
            return;
        }

        const index = _.findIndex(predictions, {
            place_id: highlighted_prediction_item.place_id,
        });
        let new_index = index + (next ? 1 : -1);

        if (new_index < 0) {
            new_index = predictions.length - 1;
        } else if (new_index > predictions.length - 1) {
            new_index = 0;
        }

        this.onPredictionHighlight(predictions[new_index]);
    }

    arePredictionsVisible() {
        const {
            force_hide_predictions,
            service_prediction_list,
            saved_address_prediction_list,
            focused_field,
            last_focused_field,
            is_mouseover_predictions,
        } = this.state;

        if (
            !force_hide_predictions &&
            (service_prediction_list.length ||
                saved_address_prediction_list.length) &&
            (focused_field === 'search_query' ||
                (last_focused_field === 'search_query' &&
                    is_mouseover_predictions &&
                    focused_field === null))
        ) {
            return true;
        }

        return false;
    }

    // eslint-disable-next-line class-methods-use-this
    canRender() {
        return !!(
            Order.getCustomer() &&
            Order.getLocation() &&
            Order.getLocation().delivery.mode === 'door_dash_drive'
        );
    }

    render() {
        if (!this.canRender()) {
            return null;
        }

        const location = Order.getLocation();
        const {
            address,
            search_query,
            focused_field,
            last_focused_field,
            is_mouseover_predictions,
            emphasis_address_unavailable_message,
            force_hide_predictions,
            is_address_available,
            is_address_availability_loading,
            wait_time,
        } = this.state;
        const { closeModal } = this.props;
        let { highlighted_prediction_item } = this.state;
        const predictions = this.getPredictions();

        if (highlighted_prediction_item === null && predictions.length) {
            [highlighted_prediction_item] = predictions;
        }

        return (
            <AddressFormComponent
                address={address}
                location={location}
                search_query={search_query}
                onInputChange={this.onInputChange}
                onInputFocus={this.onInputFocus}
                onInputBlur={this.onInputBlur}
                onInputKeyDown={this.onInputKeyDown}
                focused_field={focused_field}
                last_focused_field={last_focused_field}
                is_mouseover_predictions={is_mouseover_predictions}
                is_address_available={is_address_available}
                is_address_availability_loading={
                    is_address_availability_loading
                }
                wait_time={wait_time}
                onSaveClick={this.onSaveClick}
                emphasis_address_unavailable_message={
                    emphasis_address_unavailable_message
                }
                are_predictions_visible={this.arePredictionsVisible()}
                predictions={{
                    force_hide: force_hide_predictions,
                    list: predictions,
                    highlighted_item: highlighted_prediction_item,
                    onSelect: this.onPredictionSelect,
                    onHighlight: this.onPredictionHighlight,
                    onDelete: this.onSavedAddressDelete,
                    onMouseEnter: () =>
                        this.setState({ is_mouseover_predictions: true }),
                    onMouseLeave: () =>
                        this.setState({ is_mouseover_predictions: false }),
                }}
                closeModal={closeModal}
            />
        );
    }
}

export default forwardRefWithTranslation('customer.address.map-polygons')(
    AddressForm
);
